import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import MagicLinkLogin from '@/components/MagicLinkLogin';
import PasswordLogin from '@/components/PasswordLogin';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LoginPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Role-aware post-login routing: clients land in their tokenized portal,
  // everyone else goes back to where they came from (or /dashboard).
  const redirectAfterLogin = async (u) => {
    const from = location.state?.from;
    if (u?.role === 'client') {
      if (from && from.startsWith('/client/')) {
        window.location.href = from;
        return;
      }
      try {
        const res = await axios.get(`${API}/client/portal-entry`, { withCredentials: true });
        window.location.href = res.data.portal_url;
        return;
      } catch (e) {
        window.location.href = '/';
        return;
      }
    }
    window.location.href = from && !from.startsWith('/client/') ? from : '/dashboard';
  };

  useEffect(() => {
    if (user) redirectAfterLogin(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user) return null;

  // Check if we were just bounced from an auth redirect
  const authDebug = typeof window !== 'undefined' ? window.__AUTH_DEBUG__ : null;
  const wasAuthRedirect = authDebug && (Date.now() - authDebug.ts) < 10000 &&
    ((authDebug.hash || '').includes('session_id=') || (authDebug.search || '').includes('session_id='));

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="login-page">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_licensing-hub-3/artifacts/0s2i33uj_MM_Forsight%20Logo%20Png.png"
            alt="ForesightRx"
            className="h-11 w-auto brightness-0 mx-auto mb-4"
          />
          <p className="text-xs text-slate-400 font-medium tracking-wide uppercase">Internal Access</p>
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sign in to ForesightRx
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Authorized team members and client partners only.
          </p>
        </div>

        <Button
          onClick={handleLogin}
          className="w-full h-12 bg-[#0A1128] text-white hover:bg-slate-800 rounded-lg text-sm font-medium transition-all active:scale-[0.98] shadow-sm flex items-center justify-center gap-3"
          data-testid="google-login-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
          <ArrowRight className="w-4 h-4" />
        </Button>

        {/* Divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200" />
          </div>
          <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
            <span className="bg-slate-50 px-3 text-slate-400">or</span>
          </div>
        </div>

        {/* Email + password (staff) */}
        {!showPassword ? (
          <button
            type="button"
            onClick={() => { setShowPassword(true); setShowMagicLink(false); }}
            className="w-full h-11 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 flex items-center justify-center gap-2 transition-colors"
            data-testid="password-login-toggle"
          >
            Sign in with email &amp; password
          </button>
        ) : (
          <div className="pt-1">
            <PasswordLogin onSuccess={redirectAfterLogin} />
          </div>
        )}

        {/* Magic-link (email code) — for external collaborators whose
            corporate email isn't backed by a Google account. */}
        {!showMagicLink ? (
          <button
            type="button"
            onClick={() => { setShowMagicLink(true); setShowPassword(false); }}
            className="w-full h-11 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 flex items-center justify-center gap-2 transition-colors"
            data-testid="magic-link-toggle"
          >
            Sign in with email code
          </button>
        ) : (
          <div className="pt-1">
            <MagicLinkLogin testIdPrefix="login-magic" onSuccess={redirectAfterLogin} />
          </div>
        )}

        <p className="text-center text-xs text-slate-400">
          Looking for our public site? <a href="/" className="text-[#00709B] hover:underline font-medium">foresightrx.com</a>
        </p>

        {wasAuthRedirect && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 text-left break-all" data-testid="auth-redirect-debug">
            <p className="font-semibold mb-1">Auth redirect detected but session was lost:</p>
            <p>Initial URL: {authDebug.url}</p>
            <p>Hash: {authDebug.hash || '(empty)'}</p>
            <p>Search: {authDebug.search || '(empty)'}</p>
          </div>
        )}
      </div>
    </div>
  );
}
